/* eslint-disable no-console */
import { useAtom } from '@reatom/npm-react'
import { useEffect, useRef, useState } from 'react'
import styled from 'styled-components'

import { isTauriAtom } from '@root/shared'
import { languageAtom } from '@root/shared/localization'
import { currentThemeAtom } from '@root/shared/theme/atoms/current-theme-atom'

import { InstagramResizePostMessage } from '../model/instagram-resize-post-message'
import * as KnownSocialPlatformResource from '../model/known-social-platform-resource'
import { RedditResizePostMessage } from '../model/reddit-resize-post-message'
import { SubstackResizePostMessage } from '../model/substack-resize-post-message'
import { TelegramResizePostMessage } from '../model/telegram-resize-post-message'
import { TiktokResizePostMessage } from '../model/tiktok-resize-post-message'

const getHeightFromPostMessage =
  KnownSocialPlatformResource.matchIframeEmbedable({
    reddit: () => (data: unknown) => {
      const result = RedditResizePostMessage.decode(data)
      return result.valueOrUndefined?.data
    },
    youtube: () => () => undefined,
    tiktok: () => (data: unknown) => {
      const result = TiktokResizePostMessage.decode(data)
      return result.valueOrUndefined?.height
    },
    instagram: () => (data: unknown) => {
      const result = InstagramResizePostMessage.decode(data)
      return result.valueOrUndefined?.details.height
    },
    twitch: () => () => undefined,
    facebook: () => () => undefined,
    telegram: () => (data: unknown) => {
      const result = TelegramResizePostMessage.decode(data)
      return result.valueOrUndefined?.height
    },
    substack: () => (data: unknown) => {
      const result = SubstackResizePostMessage.decode(data)
      return result.valueOrUndefined?.iframeHeight
    },
  })

const getIframeScrollAttribute =
  KnownSocialPlatformResource.matchIframeEmbedable({
    reddit: () => 'yes',
    youtube: () => 'yes',
    tiktok: () => 'no',
    instagram: () => 'yes',
    facebook: () => 'yes',
    twitch: () => 'yes',
    telegram: () => 'no',
    substack: () => 'no',
  })

export const SocialIframeEmbed = ({
  resource,
  onLoad,
}: {
  resource: KnownSocialPlatformResource.IframeEmbedableKnownSocialPlatformResource
  onLoad: () => void
}) => {
  const sizes = resourceSizes(resource)
  const [lang] = useAtom(languageAtom)
  const [isTauri] = useAtom(isTauriAtom)
  const [currentTheme] = useAtom(currentThemeAtom)
  const appearance = currentTheme.appearance
  const iframeRef = useRef<HTMLIFrameElement | null>(null)
  const [height, setHeight] = useState<number>(sizes.height)
  const embedUrl = isTauri
    ? KnownSocialPlatformResource.toTauriUrl(resource, { lang, appearance })
    : KnownSocialPlatformResource.toIframeEmbedUrl(resource, {
        lang,
        appearance,
        youtube: {
          origin: window.location.origin,
        },
        twitch: {
          parent: window.location.hostname,
        },
      })

  const postMessageHeight = getHeightFromPostMessage(resource)

  useEffect(() => {
    const iframe = iframeRef.current
    if (iframe === null) {
      return
    }

    const platform = resource._tag
    const src = embedUrl.toString()

    const handleMessage = (event: MessageEvent) => {
      if (event.source !== iframe.contentWindow) {
        return
      }

      const pmHeight = postMessageHeight(event.data)
      if (pmHeight !== undefined) {
        setHeight(pmHeight)
      }

      console.groupCollapsed(
        `[SocialIframeEmbed:postMessage] ${platform} ← ${event.origin}`,
      )
      console.log('platform:', platform)
      console.log('src:', src)
      console.log('origin:', event.origin)
      console.log('data (raw):', event.data)
      console.log('event:', event)
      console.groupEnd()
    }

    window.addEventListener('message', handleMessage)
    return () => {
      window.removeEventListener('message', handleMessage)
    }
  }, [embedUrl, resource._tag, postMessageHeight])

  return (
    <Root
      ref={iframeRef}
      onLoad={() => {
        onLoad()
      }}
      $width={sizes.width}
      $height={height}
      loading="lazy"
      allow="
  accelerometer;
  autoplay;
  clipboard-write;
  encrypted-media;
  gyroscope;
  picture-in-picture;
  web-share
"
      sandbox="
  allow-scripts
  allow-same-origin
  allow-popups
  allow-popups-to-escape-sandbox
  allow-forms
"
      scrolling={getIframeScrollAttribute(resource)}
      frameBorder={0}
      allowFullScreen
      referrerPolicy="strict-origin-when-cross-origin"
      src={embedUrl.toString()}
    />
  )
}

export const resourceSizes = KnownSocialPlatformResource.matchIframeEmbedable({
  youtube: (resource) =>
    KnownSocialPlatformResource.matchYoutubeMediaType(resource.type, {
      onVideo: () => ({
        width: 372,
        height: 210,
      }),
      onShorts: () => ({
        width: 225,
        height: 400,
      }),
      onLive: () => ({
        width: 372,
        height: 210,
      }),
      onChannel: () => ({
        width: 372,
        height: 210,
      }),
      onPlaylist: () => ({
        width: 372,
        height: 210,
      }),
    }),
  tiktok: (resource) =>
    KnownSocialPlatformResource.matchTiktokMediaType(resource.type, {
      onVideo: () => ({
        width: 323,
        height: 739,
      }),
      onPhoto: () => ({
        width: 323,
        height: 739,
      }),
      onProfile: () => ({
        width: 372,
        height: 388,
      }),
      onTag: () => ({
        width: 360,
        height: 358,
      }),
      onSearch: () => ({
        width: 360,
        height: 358,
      }),
      onMusic: () => ({
        width: 360,
        height: 358,
      }),
    }),
  instagram: () => ({
    width: 325,
    height: 510,
  }),
  facebook: (resource) =>
    KnownSocialPlatformResource.matchFacebookMediaType(resource.type, {
      onReel: () => ({
        width: 225,
        height: 400,
      }),
      onVideo: () => ({
        width: 372,
        height: 300,
      }),
      onPost: () => ({
        width: 372,
        height: 210,
      }),
      onPage: () => ({
        width: 340,
        height: 500,
      }),
    }),

  twitch: () => ({
    width: 372,
    height: 210,
  }),
  reddit: () => ({
    width: 360,
    height: 300,
  }),
  telegram: () => ({
    width: 360,
    height: 300,
  }),
  substack: () => ({
    width: 360,
    height: 300,
  }),
})

const Root = styled.iframe<{ $width: number; $height: number }>`
  all: unset;
  position: relative;
  display: block;

  overflow: auto;
  border: none;
  width: ${({ $width }) => $width + 'px'};
  height: ${({ $height }) => $height + 'px'};
`
